import React, { useMemo, useState } from 'react';

import { FlexBox, List, Message, Orientation, Size, Text, Typography } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import { ASK_AI } from '../../keys';
import { AskSearchBestAnswer } from '../../types';
import { AskAiAuthorizationErrorCard } from './AskAiAuthorizationErrorCard';
import { AskAiAuthorizationSuccessCard } from './AskAiAuthorizationSuccessCard';

interface AskAiAuthorizationErrorsListProps {
    /** List of authorization errors of sources used in an Ask AI query */
    authErrors: AskSearchBestAnswer['authErrors'];
    /** Method used to remake an Ask AI query */
    refetch?: () => void;
}

/**
 * Component displayed in an Ask AI answer if there are `authErrors`.
 * Initially displays a list of `AskAiAuthorizationErrorCard`s for each errored connector.
 * If at least one errored connector is connected, displays `AskAiAuthorizationSuccessCard`.
 * If all errored connectors are connected, the list of `AskAiAuthorizationErrorCard`s is not displayed anymore.
 *
 * @param {AskAiAuthorizationErrorsListProps} props - Props including `authErrors` and optional `refetch` method.
 * @returns {AskAiAuthorizationErrorsList} - The rendered component.
 */
export const AskAiAuthorizationErrorsList = ({ authErrors, refetch }: AskAiAuthorizationErrorsListProps) => {
    const { translateKey } = useTranslate();

    const [erroredConnectors, setErroredConnectors] = useState<{ id: string; isConnected: boolean }[]>(
        authErrors?.map((connector) => ({
            id: connector.sourceConnector.id,
            /**
             * Ask backend is the source of truth.
             * If a connector is received in `authErrors` we automatically assume it's not connected.
             * Further authorization check is made within the `AskAiAuthorizationErrorCard` component.
             */
            isConnected: false,
        })) ?? [],
    );

    /** Boolean indicating whether at least one errored connector is connected */
    const oneConnected = useMemo(() => erroredConnectors?.some((c) => c.isConnected), [erroredConnectors]);

    /** Boolean indicating whether all errored connectors are connected */
    const allConnected = useMemo(() => {
        return !erroredConnectors?.some((c) => !c.isConnected);
    }, [erroredConnectors]);

    return (
        <FlexBox orientation={Orientation.vertical} gap={Size.big}>
            {oneConnected && <AskAiAuthorizationSuccessCard refetch={refetch} />}
            {!allConnected && (
                <Message kind="warning" hasBackground>
                    <Text as="p" typography={Typography.body1}>
                        {translateKey(ASK_AI.AUTHORIZATION_ERROR_WARNING)}
                    </Text>
                    <List>
                        {authErrors?.map((authError) => (
                            <AskAiAuthorizationErrorCard
                                key={authError.sourceConnector.id}
                                connector={authError.sourceConnector}
                                setErroredConnectors={setErroredConnectors}
                                isConnected={
                                    erroredConnectors.find((c) => c.id === authError.sourceConnector.id)?.isConnected ??
                                    false
                                }
                            />
                        ))}
                    </List>
                </Message>
            )}
        </FlexBox>
    );
};
